/**
 * Puntuación de debilidad del Día 5.
 *
 * Función PURA. Recibe las señales de la semana y devuelve `{ area, score,
 * razones }`. La interfaz imprime LAS RAZONES, nunca la fórmula.
 *
 * NO ES UN MODELO. Ordena señales que el usuario registró él mismo, y lo dice
 * así. Si no hay señales, no inventa una recomendación: lo admite y ofrece
 * elegir. Esa es la diferencia entre una herramienta en la que se puede
 * confiar y una que miente con seguridad.
 */

import { AREAS, type Area } from '../modelo/areas.js';
import { estaActivo, type ElementoBanco } from '../modelo/banco.js';
import { cuentaComoHecha, type SesionRegistrada } from '../modelo/sesion.js';

/**
 * Los pesos, en UN solo sitio.
 *
 * Si la fórmula no convence en la semana 3, se ajusta acá y en ningún otro
 * lado. Dispersarlos por el código sería garantizar que se desincronicen.
 */
export const PESOS = {
  /** Por cada elemento activo del área creado esta semana. */
  elementoNuevo: 2.0,
  /** Por cada REPETICIÓN registrada. Ver la nota sobre `vecesOcurrido`. */
  repeticion: 1.0,
  /** Por cada punto que falta hasta 5 en la autoevaluación media del área. */
  autoevaluacionBaja: 3.0,
  /** Por cada vez que el área se marcó como «lo más difícil de hoy». */
  masDificil: 2.0,
  /** Si el último checkpoint señaló esa área como debilidad. */
  checkpoint: 1.5,
  /** Si el área es parte del énfasis del bloque de semanas actual. */
  enfasisDelBloque: 0.5,
} as const;

/**
 * SEMÁNTICA DE `vecesOcurrido` — evitar el doble conteo.
 *
 * `vecesOcurrido` nace en 1: la PRIMERA aparición ya está representada por el
 * hecho de que el elemento exista. Como `elementoNuevo` ya cuenta esa primera
 * aparición, sumar `vecesOcurrido` entero la contaría dos veces y un elemento
 * recién creado valdría 3.0 en vez de 2.0.
 *
 * Por eso la señal de repetición usa `vecesOcurrido − 1`: solo las veces que
 * el usuario pulsó «volvió a pasar». Un elemento nuevo sin repeticiones aporta
 * 0 por este concepto, que es lo correcto.
 */
export function repeticionesDe(elemento: ElementoBanco): number {
  return Math.max(0, elemento.vecesOcurrido - 1);
}

// ── Señales de entrada ─────────────────────────────────────────────────────

export interface SenalesSemana {
  /** Elementos del banco creados dentro de la ventana de esta semana. */
  elementosNuevos: ElementoBanco[];
  /** Autoevaluaciones por área recogidas esta semana. */
  autoevaluaciones: Partial<Record<Area, number[]>>;
  /** Cuántas veces se marcó cada área como «lo más difícil de hoy». */
  masDificil: Partial<Record<Area, number>>;
  /** Áreas señaladas como débiles en el último checkpoint, si lo hay. */
  debilidadesDeCheckpoint: Area[];
  /** Áreas del énfasis del bloque de semanas actual. */
  areasDelBloque: Area[];
}

export function senalesVacias(): SenalesSemana {
  return {
    elementosNuevos: [],
    autoevaluaciones: {},
    masDificil: {},
    debilidadesDeCheckpoint: [],
    areasDelBloque: [],
  };
}

/**
 * Ventana de la semana: desde que empezó la primera sesión-día del ciclo.
 *
 * REGLA (§24): **el baseline no contamina la semana**. Sus dificultades siembran
 * el banco y sus autoevaluaciones son la referencia W0, pero conceptualmente no
 * pertenecen a la Semana 1: si contaran, la primera recomendación del Día 5
 * sería un eco del baseline en vez de lo que pasó entrenando.
 *
 * Devuelve `null` si todavía no empezó ninguna sesión-día del ciclo.
 */
export function inicioDeVentana(
  sesiones: SesionRegistrada[],
  semana: number,
): string | null {
  const delCiclo = sesiones
    .filter((s) => s.tipo === 'dia' && !s.deleted && s.semana === semana)
    .map((s) => s.iniciadaEn)
    .sort();
  return delCiclo[0] ?? null;
}

/** Recoge las señales de la semana a partir de los datos crudos. */
export function recogerSenales(opciones: {
  sesiones: SesionRegistrada[];
  banco: ElementoBanco[];
  semana: number;
  areasDelBloque: Area[];
  debilidadesDeCheckpoint?: Area[];
}): SenalesSemana {
  const { sesiones, banco, semana, areasDelBloque } = opciones;
  const desde = inicioDeVentana(sesiones, semana);

  // Solo sesiones-DÍA de esta semana que cuentan como hechas. El baseline y
  // los checkpoints quedan fuera a propósito.
  const delCiclo = sesiones.filter(
    (s) => s.tipo === 'dia' && s.semana === semana && cuentaComoHecha(s),
  );

  const autoevaluaciones: Partial<Record<Area, number[]>> = {};
  const masDificil: Partial<Record<Area, number>> = {};

  for (const sesion of delCiclo) {
    for (const [area, valor] of Object.entries(sesion.autoevaluaciones)) {
      if (typeof valor !== 'number') continue;
      const clave = area as Area;
      (autoevaluaciones[clave] ??= []).push(valor);
    }
    if (sesion.masDificilArea) {
      masDificil[sesion.masDificilArea] = (masDificil[sesion.masDificilArea] ?? 0) + 1;
    }
  }

  const elementosNuevos = desde
    ? banco.filter(
        (e) =>
          estaActivo(e) &&
          e.area !== null &&
          // El baseline siembra antes de la semana: es material, no señal.
          e.fuente !== 'baseline' &&
          e.creadoEn >= desde,
      )
    : [];

  return {
    elementosNuevos,
    autoevaluaciones,
    masDificil,
    debilidadesDeCheckpoint: opciones.debilidadesDeCheckpoint ?? [],
    areasDelBloque,
  };
}

// ── Puntuación ─────────────────────────────────────────────────────────────

export interface Razon {
  /** Frase en español llano, construida a partir del dato REAL. */
  texto: string;
  /** Cuánto aportó al total. Para pruebas; la interfaz no lo muestra. */
  puntos: number;
}

export interface PuntuacionArea {
  area: Area;
  puntuacion: number;
  razones: Razon[];
  /** Media de autoevaluación del área esta semana, si la hay. */
  promedioAutoevaluacion: number | null;
  /** Elementos nuevos del área esta semana. */
  elementosNuevos: number;
}

function redondear(valor: number): number {
  return Math.round(valor * 100) / 100;
}

export function puntuarArea(area: Area, senales: SenalesSemana): PuntuacionArea {
  const razones: Razon[] = [];
  let total = 0;

  const nuevos = senales.elementosNuevos.filter((e) => e.area === area);
  if (nuevos.length > 0) {
    const puntos = nuevos.length * PESOS.elementoNuevo;
    total += puntos;
    razones.push({
      texto:
        nuevos.length === 1
          ? 'Registraste 1 dificultad nueva de esta área esta semana.'
          : `Registraste ${nuevos.length} dificultades nuevas de esta área esta semana.`,
      puntos,
    });
  }

  const repeticiones = nuevos.reduce((suma, e) => suma + repeticionesDe(e), 0);
  if (repeticiones > 0) {
    const puntos = repeticiones * PESOS.repeticion;
    total += puntos;
    razones.push({
      texto:
        repeticiones === 1
          ? 'Marcaste 1 vez que algo de esta área te volvió a pasar.'
          : `Marcaste ${repeticiones} veces que algo de esta área te volvió a pasar.`,
      puntos,
    });
  }

  const puntuaciones = senales.autoevaluaciones[area] ?? [];
  const promedio =
    puntuaciones.length > 0
      ? puntuaciones.reduce((a, b) => a + b, 0) / puntuaciones.length
      : null;
  if (promedio !== null) {
    const puntos = (5 - promedio) * PESOS.autoevaluacionBaja;
    total += puntos;
    if (puntos > 0) {
      razones.push({
        texto: `Tu autoevaluación promedio en esta área fue ${promedio.toFixed(1)}/5.`,
        puntos,
      });
    }
  }

  const veces = senales.masDificil[area] ?? 0;
  if (veces > 0) {
    const puntos = veces * PESOS.masDificil;
    total += puntos;
    razones.push({
      texto:
        veces === 1
          ? 'La marcaste una vez como «lo más difícil de hoy».'
          : `La marcaste ${veces} veces como «lo más difícil de hoy».`,
      puntos,
    });
  }

  if (senales.debilidadesDeCheckpoint.includes(area)) {
    total += PESOS.checkpoint;
    razones.push({
      texto: 'Apareció como punto flojo en tu último checkpoint.',
      puntos: PESOS.checkpoint,
    });
  }

  if (senales.areasDelBloque.includes(area)) {
    total += PESOS.enfasisDelBloque;
    razones.push({
      texto: 'Es una de las áreas que enfatiza este bloque de semanas.',
      puntos: PESOS.enfasisDelBloque,
    });
  }

  return {
    area,
    puntuacion: redondear(total),
    razones,
    promedioAutoevaluacion: promedio,
    elementosNuevos: nuevos.length,
  };
}

/**
 * Señales que cuentan de verdad, es decir, sin el énfasis del bloque.
 *
 * El énfasis solo desempata: si lo único que tiene un área es ese 0.5, no hay
 * datos, y decir lo contrario sería inventar una recomendación.
 */
function tieneSenalesReales(p: PuntuacionArea): boolean {
  return p.razones.some((r) => r.texto !== 'Es una de las áreas que enfatiza este bloque de semanas.');
}

export type Recomendacion =
  | { tipo: 'sugerencia'; mejor: PuntuacionArea; todas: PuntuacionArea[] }
  | { tipo: 'sin_datos'; todas: PuntuacionArea[] };

/**
 * DESEMPATE, determinista y en este orden:
 *
 *   1. puntuación más alta;
 *   2. autoevaluación promedio más baja (una señal directa y reciente);
 *   3. más elementos nuevos esta semana;
 *   4. orden canónico de las áreas, como último recurso técnico.
 *
 * Nunca al azar: dos ejecuciones con los mismos datos dan el mismo resultado.
 */
export function compararPuntuaciones(a: PuntuacionArea, b: PuntuacionArea): number {
  if (b.puntuacion !== a.puntuacion) return b.puntuacion - a.puntuacion;

  const promedioA = a.promedioAutoevaluacion ?? Infinity;
  const promedioB = b.promedioAutoevaluacion ?? Infinity;
  if (promedioA !== promedioB) return promedioA - promedioB;

  if (b.elementosNuevos !== a.elementosNuevos) return b.elementosNuevos - a.elementosNuevos;

  return AREAS.indexOf(a.area) - AREAS.indexOf(b.area);
}

export function recomendarFoco(senales: SenalesSemana): Recomendacion {
  const todas = AREAS.map((area) => puntuarArea(area, senales)).sort(compararPuntuaciones);
  const mejor = todas[0]!;

  if (mejor.puntuacion <= 0 || !tieneSenalesReales(mejor)) {
    return { tipo: 'sin_datos', todas };
  }
  return { tipo: 'sugerencia', mejor, todas };
}

export const MENSAJE_SIN_DATOS =
  'Todavía no tenemos suficientes datos de esta semana para recomendar un foco.';
